import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconArrowLeft, IconPlus } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../../toolbar';
import type {
  AdditionalField,
  BillingMode,
  BillingStrategy,
  TemplateFollowUpTodo,
  TemplateLineItem,
  TicketTypeTemplate,
} from '../../../types/ticketTypeTemplate';
import { slugify } from '../../../utils/slugify';
import { AdditionalFieldEditor } from './AdditionalFieldEditor';
import { TemplateLineItemEditor } from './TemplateLineItemEditor';
import { FollowUpTodoEditor } from './FollowUpTodoEditor';
import { HelpHint } from '../HelpHint';
import { useProductCatalog } from '../../../hooks/useProductCatalog';
import { CARD_SX, FIELD_SX, OUTLINED_BTN_SX } from '../../../styles/designSystem';
import '../EditorCard.css';
import { uniqueCopyKey } from '../../../utils/uniqueCopyKey';

// Hover-help copy for each editor field. Kept next to the component (mirrors
// CreateTicketTypeTemplateDialog) because the wording is tightly coupled to
// how each field is consumed when a ticket is raised from the template.
const HELP = {
  displayName:
    'Human-friendly name shown to techs in pickers and headings ' +
    '(e.g. "Microsoft 365 Onboarding"). Also used as the ticket subject ' +
    'when no Subject Template is set.',
  ticketTypeId:
    'Stable identifier used in URLs, routing, and as the foreign key from ' +
    'work items, task configs and customer overrides (snake_case, e.g. ' +
    'm365_onboarding). Set at creation and immutable — existing tickets ' +
    'and templates reference it directly.',
  group:
    'Category used to group this template in the ticket-type picker ' +
    '(e.g. "Onboarding", "Repair", "Project"), and sent as the ticket\'s ' +
    'job type on creation. Pick an existing group to slot it next to ' +
    'siblings, or type a new one to start a group.',
  serviceKey:
    'Controls which customers see this template. The picker only shows ' +
    'templates whose serviceKey matches one of the customer\'s ACTIVE ' +
    'services (e.g. "M365", "Google", "Backup", "Hosting"); match is ' +
    'case-insensitive. Use the special value "other" to always show this ' +
    'template regardless of the customer\'s services.',
  templateVersion:
    'Free-form version stamp (e.g. "1.0") carried onto every ticket raised ' +
    'from this template. Bump it when you materially change the line items ' +
    'or fields so raised tickets trace back to the revision that produced ' +
    'them.',
  billingMode:
    'How time/effort is captured for invoicing. "hours" — bill by recorded ' +
    'hours. "units" — bill by discrete units (licences, devices, jobs).',
  billingStrategy:
    'Pricing rule applied at invoice time. "none" — bill flat from line ' +
    'items. "remote-support" — applies the remote-support rate matrix. ' +
    '"fixed-labour" — caps labour at a fixed amount. "per-license" — ' +
    'multiplies by the licence quantity on the ticket.',
  subjectTemplate:
    'Template for the ticket subject. Supports {fieldKey} placeholders that ' +
    'interpolate Additional Field values (e.g. "M365 onboarding — ' +
    '{companyName}"). Leave blank to use the Display Name as the subject.',
  descriptionTemplate:
    'Template for the ticket description/body. Supports {fieldKey} ' +
    'placeholders and {?fieldKey}…{/fieldKey} conditional blocks (the ' +
    'block is kept only when that field has a value). Leave blank to fall ' +
    'back to the Default Ticket Description.',
  defaultTicketDescription:
    'Static description used verbatim when no Description Template is set ' +
    '(and as the fallback body). Plain text — {fieldKey} placeholders are ' +
    'NOT interpolated here.',
  additionalFields:
    'Extra inputs techs fill in when raising a ticket from this template ' +
    '(text, dropdown, number, date, licence picker). Each field\'s key ' +
    'feeds the Subject / Description templates and line-item prefixes via ' +
    '{fieldKey}.',
  templateLineItems:
    'Charge lines pre-populated on every ticket raised from this template. ' +
    'Each maps to a product / billing code with a default quantity, ' +
    'optional pricing rule and interpolated line detail.',
  newField:
    'Label is what techs see; the field key is the snake_case identifier ' +
    'referenced by {fieldKey} in templates and pricing rules. Leave the key ' +
    'blank to auto-derive it from the label.',
  followUpTodos:
    'To-dos created alongside every ticket raised from this template. Each ' +
    'raises a ticket from another template on one of this template\'s date ' +
    'fields plus an offset (e.g. remove a Conditional Access country the day ' +
    'after End Date), then marks itself done. Fields the two templates share ' +
    'by key are copied across. Needs at least one Date field.',
  newLineItem:
    'Enter a unique template key — a snake_case identifier for this line ' +
    '(e.g. "labour_default"). Configure its product code, quantity and ' +
    'pricing after adding.',
} as const;

interface TicketTypeTemplateDetailEditorProps {
  template: TicketTypeTemplate;
  /** Other templates a follow-up ticket can be raised from. */
  followUpTargets?: { ticketTypeId: string; displayName: string }[];
  groupSuggestions: string[];
  serviceKeySuggestions: string[];
  onChange: (updated: TicketTypeTemplate) => void;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}

const BILLING_MODES: BillingMode[] = ['hours', 'units'];
const BILLING_STRATEGIES: BillingStrategy[] = ['none', 'remote-support', 'fixed-labour', 'per-license'];

const CARD_FORM_SX = { ...CARD_SX, p: 2 };

export const TicketTypeTemplateDetailEditor: React.FC<TicketTypeTemplateDetailEditorProps> = ({
  template,
  followUpTargets = [],
  groupSuggestions,
  serviceKeySuggestions,
  onChange,
  onBack,
  onSave,
  saving,
}) => {
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newLineItemKey, setNewLineItemKey] = useState('');

  // Loaded once here and passed down to every line-item editor, so the Line
  // Item ID picker shares the catalog the ticket line-item picker uses.
  const { products } = useProductCatalog();

  const fields = template.additionalFields ?? [];
  const lineItems = template.templateLineItems ?? [];

  const setField = <K extends keyof TicketTypeTemplate>(
    key: K,
    value: TicketTypeTemplate[K],
  ) => {
    onChange({ ...template, [key]: value });
  };

  // --- Field array helpers ---

  const addField = () => {
    const label = newFieldLabel.trim();
    const key = newFieldKey.trim() || slugify(label).replace(/-/g, '');
    if (!label || !key) return;
    if (fields.some((f) => f.fieldKey === key)) return;
    const field: AdditionalField = {
      fieldKey: key,
      label,
      type: 'text',
      required: 'optional',
    };
    setField('additionalFields', [...fields, field]);
    setNewFieldLabel('');
    setNewFieldKey('');
  };

  const updateField = (index: number, updated: AdditionalField) => {
    const next = [...fields];
    next[index] = updated;
    setField('additionalFields', next);
  };

  const removeField = (index: number) => {
    setField(
      'additionalFields',
      fields.filter((_, i) => i !== index),
    );
  };

  const duplicateField = (index: number) => {
    const source = fields[index];
    const candidateKey = uniqueCopyKey(source.fieldKey, (key) =>
      fields.some((f) => f.fieldKey === key),
    );
    const copy: AdditionalField = { ...source, fieldKey: candidateKey };
    const next = [...fields];
    next.splice(index + 1, 0, copy);
    setField('additionalFields', next);
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    setField('additionalFields', next);
  };

  // --- Line item array helpers ---

  const addLineItem = () => {
    const key = newLineItemKey.trim() || `item_${lineItems.length + 1}`;
    if (lineItems.some((li) => li.templateKey === key)) return;
    const item: TemplateLineItem = {
      templateKey: key,
      lineItemId: '',
      category: 'labour',
      defaultQuantity: 1,
    };
    setField('templateLineItems', [...lineItems, item]);
    setNewLineItemKey('');
  };

  const updateLineItem = (index: number, updated: TemplateLineItem) => {
    const next = [...lineItems];
    next[index] = updated;
    setField('templateLineItems', next);
  };

  const removeLineItem = (index: number) => {
    setField(
      'templateLineItems',
      lineItems.filter((_, i) => i !== index),
    );
  };

  const duplicateLineItem = (index: number) => {
    const source = lineItems[index];
    const candidateKey = uniqueCopyKey(source.templateKey, (key) =>
      lineItems.some((li) => li.templateKey === key),
    );
    const copy: TemplateLineItem = { ...source, templateKey: candidateKey };
    const next = [...lineItems];
    next.splice(index + 1, 0, copy);
    setField('templateLineItems', next);
  };

  const moveLineItem = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= lineItems.length) return;
    const next = [...lineItems];
    [next[index], next[target]] = [next[target], next[index]];
    setField('templateLineItems', next);
  };

  const siblingFieldKeys = fields.map((f) => f.fieldKey);

  // --- Follow-up to-do helpers ---

  const followUps = template.followUpTodos ?? [];
  const dateFields = fields
    .filter((f) => f.type === 'date')
    .map((f) => ({ fieldKey: f.fieldKey, label: f.label }));

  const addFollowUp = () => {
    const followUp: TemplateFollowUpTodo = {
      ticketTypeId: followUpTargets[0]?.ticketTypeId ?? '',
      dateField: dateFields[0]?.fieldKey ?? '',
      offsetDays: 1,
      titleTemplate: '',
      priority: 'medium',
    };
    setField('followUpTodos', [...followUps, followUp]);
  };

  const updateFollowUp = (index: number, updated: TemplateFollowUpTodo) => {
    const next = [...followUps];
    next[index] = updated;
    setField('followUpTodos', next);
  };

  const removeFollowUp = (index: number) => {
    setField(
      'followUpTodos',
      followUps.filter((_, i) => i !== index),
    );
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarButton icon={IconArrowLeft} onClick={onBack}>
          Back to list
        </ToolbarButton>
        <Typography sx={{ ml: 0.5, fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
          {template.displayName}
        </Typography>
        <ToolbarSpacer />
        <ToolbarButton variant="primary" onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={CARD_FORM_SX}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 2 }}>
            Core
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Stack direction="row" alignItems="flex-start" sx={{ flex: 1 }}>
                <TextField
                  label="Display Name"
                  value={template.displayName}
                  onChange={(e) => setField('displayName', e.target.value)}
                  required
                  size="small"
                  sx={[{ flex: 1 }, FIELD_SX]}
                />
                <HelpHint text={HELP.displayName} />
              </Stack>
              <Stack direction="row" alignItems="flex-start">
                <TextField
                  label="Ticket Type ID"
                  value={template.ticketTypeId}
                  size="small"
                  slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace' } } }}
                  helperText="Set at creation"
                  sx={[{ width: 240 }, FIELD_SX]}
                />
                <HelpHint text={HELP.ticketTypeId} />
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Stack direction="row" alignItems="flex-start" sx={{ flex: 1 }}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={groupSuggestions}
                  value={template.group}
                  onChange={(_, v) => setField('group', v ?? '')}
                  onInputChange={(_, v) => setField('group', v)}
                  sx={[{ flex: 1 }, FIELD_SX]}
                  renderInput={(params) => <TextField {...params} label="Group" required />}
                />
                <HelpHint text={HELP.group} />
              </Stack>
              <Stack direction="row" alignItems="flex-start" sx={{ flex: 1 }}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={serviceKeySuggestions}
                  value={template.serviceKey}
                  onChange={(_, v) => setField('serviceKey', v ?? '')}
                  onInputChange={(_, v) => setField('serviceKey', v)}
                  sx={[{ flex: 1 }, FIELD_SX]}
                  renderInput={(params) => <TextField {...params} label="Service Key" required />}
                />
                <HelpHint text={HELP.serviceKey} />
              </Stack>
              <Stack direction="row" alignItems="flex-start">
                <TextField
                  label="Template Version"
                  value={String(template.templateVersion)}
                  onChange={(e) => setField('templateVersion', e.target.value)}
                  size="small"
                  sx={[{ width: 160 }, FIELD_SX]}
                />
                <HelpHint text={HELP.templateVersion} />
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
                <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                  <InputLabel>Billing Mode</InputLabel>
                  <Select
                    label="Billing Mode"
                    value={template.billingMode}
                    onChange={(e) => setField('billingMode', e.target.value as BillingMode)}
                  >
                    {BILLING_MODES.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpHint text={HELP.billingMode} />
              </Stack>
              <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
                <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                  <InputLabel>Billing Strategy</InputLabel>
                  <Select
                    label="Billing Strategy"
                    value={template.billingStrategy}
                    onChange={(e) => setField('billingStrategy', e.target.value as BillingStrategy)}
                  >
                    {BILLING_STRATEGIES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpHint text={HELP.billingStrategy} />
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="flex-start">
              <TextField
                label="Subject Template"
                value={template.subjectTemplate ?? ''}
                onChange={(e) => setField('subjectTemplate', e.target.value || undefined)}
                fullWidth
                size="small"
                helperText="Supports {fieldKey} interpolation. Blank → uses Display Name."
                sx={FIELD_SX}
              />
              <HelpHint text={HELP.subjectTemplate} />
            </Stack>
            <Stack direction="row" alignItems="flex-start">
              <TextField
                label="Description Template"
                value={template.descriptionTemplate ?? ''}
                onChange={(e) => setField('descriptionTemplate', e.target.value || undefined)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Supports {fieldKey} and {?field}…{/field} conditional blocks. Blank → uses Default Ticket Description."
                sx={FIELD_SX}
              />
              <HelpHint text={HELP.descriptionTemplate} />
            </Stack>
            <Stack direction="row" alignItems="flex-start">
              <TextField
                label="Default Ticket Description"
                value={template.defaultTicketDescription ?? ''}
                onChange={(e) =>
                  setField('defaultTicketDescription', e.target.value || undefined)
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Static fallback body used when Description Template is blank. No interpolation."
                sx={FIELD_SX}
              />
              <HelpHint text={HELP.defaultTicketDescription} />
            </Stack>
          </Stack>
        </Box>

        {/* Additional fields */}
        <Box sx={{ px: '2px', pt: '8px', display: 'flex', alignItems: 'center' }}>
          <span className="editor-section-title">Additional Fields ({fields.length})</span>
          <HelpHint text={HELP.additionalFields} />
        </Box>
        {fields.length === 0 && (
          <div className="editor-empty">No fields yet. Add one below.</div>
        )}
        {fields.map((field, index) => (
          <AdditionalFieldEditor
            key={field.fieldKey}
            field={field}
            siblingFieldKeys={siblingFieldKeys}
            onChange={(updated) => updateField(index, updated)}
            onDelete={() => removeField(index)}
            onDuplicate={() => duplicateField(index)}
            onMoveUp={index > 0 ? () => moveField(index, 'up') : undefined}
            onMoveDown={index < fields.length - 1 ? () => moveField(index, 'down') : undefined}
          />
        ))}
        <Box sx={CARD_FORM_SX}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              size="small"
              label="New field label"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              sx={[{ flex: 1 }, FIELD_SX]}
            />
            <TextField
              size="small"
              label="Field key"
              value={newFieldKey}
              onChange={(e) => setNewFieldKey(e.target.value)}
              sx={[{ width: 200 }, FIELD_SX]}
              slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              placeholder={newFieldLabel ? slugify(newFieldLabel).replace(/-/g, '') : undefined}
            />
            <HelpHint text={HELP.newField} />
            <Button
              variant="outlined"
              startIcon={<IconPlus size={16} stroke={1.5} />}
              onClick={addField}
              disabled={!newFieldLabel.trim()}
              sx={[OUTLINED_BTN_SX, { mt: '1px', flexShrink: 0 }]}
            >
              Add Field
            </Button>
          </Stack>
        </Box>

        {/* Line items */}
        <Box sx={{ px: '2px', pt: '8px', display: 'flex', alignItems: 'center' }}>
          <span className="editor-section-title">Template Line Items ({lineItems.length})</span>
          <HelpHint text={HELP.templateLineItems} />
        </Box>
        {lineItems.length === 0 && (
          <div className="editor-empty">No line items yet. Add one below.</div>
        )}
        {lineItems.map((item, index) => (
          <TemplateLineItemEditor
            key={item.templateKey}
            item={item}
            products={products}
            onChange={(updated) => updateLineItem(index, updated)}
            onDelete={() => removeLineItem(index)}
            onDuplicate={() => duplicateLineItem(index)}
            onMoveUp={index > 0 ? () => moveLineItem(index, 'up') : undefined}
            onMoveDown={
              index < lineItems.length - 1 ? () => moveLineItem(index, 'down') : undefined
            }
          />
        ))}
        <Box sx={CARD_FORM_SX}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              size="small"
              label="New line item template key"
              value={newLineItemKey}
              onChange={(e) => setNewLineItemKey(e.target.value)}
              sx={[{ flex: 1 }, FIELD_SX]}
              slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              placeholder={`item_${lineItems.length + 1}`}
            />
            <HelpHint text={HELP.newLineItem} />
            <Button
              variant="outlined"
              startIcon={<IconPlus size={16} stroke={1.5} />}
              onClick={addLineItem}
              sx={[OUTLINED_BTN_SX, { mt: '1px', flexShrink: 0 }]}
            >
              Add Line Item
            </Button>
          </Stack>
        </Box>

        {/* Follow-up to-dos */}
        <Box sx={{ px: '2px', pt: '8px', display: 'flex', alignItems: 'center' }}>
          <span className="editor-section-title">Follow-up To-dos ({followUps.length})</span>
          <HelpHint text={HELP.followUpTodos} />
        </Box>
        {followUps.length === 0 && (
          <div className="editor-empty">
            {dateFields.length === 0
              ? 'Add a Date field above to schedule a follow-up from it.'
              : 'No follow-ups. Add one below.'}
          </div>
        )}
        {followUps.map((followUp, index) => (
          <FollowUpTodoEditor
            // Follow-ups have no stable key of their own; the list is only edited here.
            key={index}
            followUp={followUp}
            targets={followUpTargets}
            dateFields={dateFields}
            onChange={(updated) => updateFollowUp(index, updated)}
            onDelete={() => removeFollowUp(index)}
          />
        ))}
        <Box sx={CARD_FORM_SX}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1, fontSize: 13, color: '#888' }}>
              The follow-up ticket's template must be saved before this one.
            </Box>
            <Button
              variant="outlined"
              startIcon={<IconPlus size={16} stroke={1.5} />}
              onClick={addFollowUp}
              disabled={dateFields.length === 0 || followUpTargets.length === 0}
              sx={[OUTLINED_BTN_SX, { flexShrink: 0 }]}
            >
              Add Follow-up
            </Button>
          </Stack>
        </Box>
      </Box>
    </>
  );
};
